import React from 'react';
import { COLORS, GRADIENTS, SHADOWS, RADII, apexFontFamily } from './chartTokens';

// ─────────────────────────────────────────────────────────────
// ChartSkeleton – Animated placeholder while chart data loads
// Props:
//   type: 'bars' | 'lines' | 'cards'   (default: 'bars')
//   rows: number of skeleton rows
// ─────────────────────────────────────────────────────────────

const pulse = `
  @keyframes chartSkeletonPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }
  @keyframes chartShimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const shimmerStyle = {
  background: `linear-gradient(90deg, ${COLORS.skeletonBg} 25%, #E8EDF5 50%, ${COLORS.skeletonBg} 75%)`,
  backgroundSize: '800px 100%',
  animation: 'chartShimmer 1.6s infinite linear',
  borderRadius: '6px',
};

const SkeletonBlock = ({ w = '100%', h = '16px', mb = '0', borderRadius = '6px', style = {} }) => (
  <div style={{ ...shimmerStyle, width: w, height: h, marginBottom: mb, borderRadius, ...style }} />
);

// ── Bar skeleton ──────────────────────────────────────────────────────────────
const BarsSkeleton = ({ rows = 5 }) => (
  <div style={{ padding: '16px 0' }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <SkeletonBlock w="90px" h="14px" />
        <SkeletonBlock w={`${40 + Math.random() * 50}%`} h="28px" borderRadius="6px" />
      </div>
    ))}
  </div>
);

// ── Line/Area skeleton ────────────────────────────────────────────────────────
const LinesSkeleton = ({ rows = 3 }) => (
  <div style={{ padding: '16px 0' }}>
    <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', justifyContent: 'center' }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SkeletonBlock w="10px" h="10px" borderRadius="50%" />
          <SkeletonBlock w="80px" h="12px" />
        </div>
      ))}
    </div>
    <div style={{ position: 'relative', height: '280px', background: COLORS.skeletonBg, borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ ...shimmerStyle, position: 'absolute', inset: 0, borderRadius: '8px' }} />
      {/* Fake data points */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none">
        <polyline
          points="0,200 80,140 160,160 240,100 320,120 400,60 480,80 560,40"
          fill="none" stroke="rgba(99,102,241,0.15)" strokeWidth="3"
        />
        <polyline
          points="0,240 80,200 160,210 240,170 320,180 400,130 480,150 560,110"
          fill="none" stroke="rgba(16,185,129,0.15)" strokeWidth="3"
        />
      </svg>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonBlock key={i} w="60px" h="12px" />
      ))}
    </div>
  </div>
);

// ── Cards skeleton ─────────────────────────────────────────────────────────────
const CardsSkeleton = ({ count = 4 }) => (
  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} style={{
        flex: '1 1 200px',
        background: COLORS.cardBg,
        borderRadius: RADII.card,
        padding: '24px',
        boxShadow: SHADOWS.card,
        border: `1px solid ${COLORS.border}`,
      }}>
        <SkeletonBlock w="48px" h="48px" borderRadius="12px" mb="16px" />
        <SkeletonBlock w="50%" h="28px" mb="8px" />
        <SkeletonBlock w="70%" h="14px" />
      </div>
    ))}
  </div>
);

const ChartSkeleton = ({ type = 'bars', rows = 5 }) => (
  <>
    <style>{pulse}</style>
    {type === 'bars'  && <BarsSkeleton rows={rows} />}
    {type === 'lines' && <LinesSkeleton />}
    {type === 'cards' && <CardsSkeleton count={rows} />}
  </>
);

export default ChartSkeleton;
